/**
 * DSettings > "Dynamic Input" tab.
 *
 * Full AutoCAD 2024 layout with four sections:
 *   1. Pointer Input — DYNMODE bit 1 + Settings... subdialog (DYNPICOORDS/DYNPIFORMAT/DYNPIVIS)
 *   2. Dimension Input — DYNMODE bit 2 + Settings... subdialog (DYNDIVIS)
 *   3. Dynamic Prompts — DYNPROMPT checkbox
 *   4. Drafting Tooltip Appearance — placeholder button (honest stub, labelled correctly)
 *
 * Each checkbox reads and writes the corresponding sysvar via useSysvarStore.
 * The "Settings..." buttons open nested modal sub-dialogs rendered inline.
 */

import { useState } from "react";

import { useT } from "../../i18n";
import { useSysvarStore } from "../sysvarStore";
import { PointerInputSettingsSubdialog } from "./PointerInputSettingsSubdialog";
import { DimInputSettingsSubdialog } from "./DimInputSettingsSubdialog";

export function DynamicInputTab() {
  const t = useT();

  const dynMode = useSysvarStore((s) => s.values.DYNMODE);
  const dynPrompt = useSysvarStore((s) => s.values.DYNPROMPT);
  const setVar = useSysvarStore((s) => s.setVar);

  const pointerEnabled = (dynMode & 1) !== 0;
  const dimEnabled = (dynMode & 2) !== 0;

  const [piSettingsOpen, setPiSettingsOpen] = useState(false);
  const [diSettingsOpen, setDiSettingsOpen] = useState(false);

  function togglePointerBit(checked: boolean) {
    const next = checked ? dynMode | 1 : dynMode & ~1;
    setVar("DYNMODE", next);
  }

  function toggleDimBit(checked: boolean) {
    const next = checked ? dynMode | 2 : dynMode & ~2;
    setVar("DYNMODE", next);
  }

  return (
    <div className="dsettings-tab-content">
      {/* ── Section 1: Pointer Input ── */}
      <div className="dsettings-section">
        <div className="dsettings-section-title">
          {t("dsettings.dyninput.pointer.title")}
        </div>
        <div className="dsettings-section-row">
          <label className="dsettings-checkbox-row">
            <input
              type="checkbox"
              checked={pointerEnabled}
              onChange={(e) => togglePointerBit(e.target.checked)}
              data-testid="dsettings-pointer-enable"
            />
            <span className="dsettings-checkbox-label">
              {t("dsettings.dyninput.pointer.enable")}
            </span>
          </label>
          <button
            type="button"
            className="dsettings-settings-btn"
            onClick={() => setPiSettingsOpen(true)}
            disabled={!pointerEnabled}
            data-testid="dsettings-pointer-settings"
          >
            {t("dsettings.dyninput.pointer.settings")}
          </button>
        </div>
      </div>

      {/* ── Section 2: Dimension Input ── */}
      <div className="dsettings-section">
        <div className="dsettings-section-title">
          {t("dsettings.dyninput.dim.title")}
        </div>
        <div className="dsettings-section-row">
          <label className="dsettings-checkbox-row">
            <input
              type="checkbox"
              checked={dimEnabled}
              onChange={(e) => toggleDimBit(e.target.checked)}
              data-testid="dsettings-dim-enable"
            />
            <span className="dsettings-checkbox-label">
              {t("dsettings.dyninput.dim.enable")}
            </span>
          </label>
          <button
            type="button"
            className="dsettings-settings-btn"
            onClick={() => setDiSettingsOpen(true)}
            disabled={!dimEnabled}
            data-testid="dsettings-dim-settings"
          >
            {t("dsettings.dyninput.dim.settings")}
          </button>
        </div>
      </div>

      {/* ── Section 3: Dynamic Prompts ── */}
      <div className="dsettings-section">
        <div className="dsettings-section-title">
          {t("dsettings.dyninput.prompt.title")}
        </div>
        <label className="dsettings-checkbox-row">
          <input
            type="checkbox"
            checked={dynPrompt !== 0}
            onChange={(e) => setVar("DYNPROMPT", e.target.checked ? 1 : 0)}
            data-testid="dsettings-dynprompt-enable"
          />
          <span className="dsettings-checkbox-label">
            {t("dsettings.dyninput.prompt.enable")}
          </span>
        </label>
      </div>

      {/* ── Section 4: Drafting Tooltip Appearance ── */}
      <div className="dsettings-section">
        <button
          type="button"
          className="dsettings-appearance-btn"
          onClick={() => {
            /* Phase 4: color/size tooltip appearance dialog */
          }}
          data-testid="dsettings-appearance"
        >
          {t("dsettings.dyninput.appearance")}
        </button>
      </div>

      {/* ── Sub-dialogs ── */}
      {piSettingsOpen ? (
        <PointerInputSettingsSubdialog onClose={() => setPiSettingsOpen(false)} />
      ) : null}
      {diSettingsOpen ? (
        <DimInputSettingsSubdialog onClose={() => setDiSettingsOpen(false)} />
      ) : null}
    </div>
  );
}
